<div>
    @if (metricsLoadError) {
        <div class="alert alert-warning py-2 small" role="alert">Dashboard metrics could not be loaded. Try refreshing the page.</div>
    }

    <div class="row g-3 mb-3">
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Total damages</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.totalDamages }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">This month</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.damagesThisMonth }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Last 30 days</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.damagesLast30Days }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Year to date</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.damagesYearToDate }}</div>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-danger border-opacity-50">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">VanCon at fault</div>
                    <div class="fs-3 fw-semibold text-danger">{{ metrics.kpi.vanConAtFaultCount }}</div>
                    <div class="small text-muted">{{ metrics.kpi.vanConAtFaultRate }}% of damages</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-warning border-opacity-50">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Damaged before excavation</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.damagedBeforeExcavationCount }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-warning border-opacity-50">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Outside 2 ft tolerance</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.outsideToleranceCount }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-secondary border-opacity-50">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Marked incorrectly</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.markedIncorrectlyCount }}</div>
                    <div class="small text-muted">{{ metrics.kpi.prePhotosNotTakenCount }} without pre-photos</div>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3">
        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Damages by month</h5>
                    <p class="small text-muted">Damaged utilities over the last 12 months.</p>
                    @if (metrics.monthlyCounts.length === 0) {
                        <p class="text-muted small mb-0">No damages in this period.</p>
                    } @else {
                        <div class="d-flex align-items-end gap-1 du-bar-wrap">
                            @for (m of metrics.monthlyCounts; track m.label) {
                                <div class="flex-fill text-center">
                                    <div class="du-bar rounded-top" [style.height.px]="(m.count / maxMonthlyCount()) * 120 || 4" [title]="m.label + ': ' + m.count"></div>
                                    <div class="du-bar-label text-muted mt-1">{{ m.label }}</div>
                                    <div class="small fw-semibold">{{ m.count }}</div>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>

        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Top jobs by damages</h5>
                    <p class="small text-muted">Jobs with the most damaged utilities.</p>
                    @if (metrics.topJobs.length === 0) {
                        <p class="text-muted small mb-0">No damages recorded yet.</p>
                    } @else {
                        <ul class="list-unstyled mb-0">
                            @for (job of metrics.topJobs; track job.jobId) {
                                <li class="d-flex justify-content-between border-bottom py-1">
                                    <span class="text-truncate me-2">{{ job.jobName }}</span>
                                    <span class="fw-semibold">{{ job.count }}</span>
                                </li>
                            }
                        </ul>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mt-0">
        <div class="col-lg-4">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">By utility type</h5>
                    @if (metrics.byUtilityType.length === 0) {
                        <p class="text-muted small mb-0">No utility types recorded yet.</p>
                    } @else {
                        @for (item of metrics.byUtilityType; track item.label) {
                            <div class="mb-2">
                                <div class="d-flex justify-content-between small">
                                    <span class="text-truncate me-2">{{ item.label }}</span>
                                    <span class="fw-semibold">{{ item.count }}</span>
                                </div>
                                <div class="du-track">
                                    <div class="du-track-fill" [style.width.%]="(item.count / maxLabelCount(metrics.byUtilityType)) * 100"></div>
                                </div>
                            </div>
                        }
                    }
                </div>
            </div>
        </div>

        <div class="col-lg-4">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">By utility owner</h5>
                    @if (metrics.byUtilityOwner.length === 0) {
                        <p class="text-muted small mb-0">No utility owners recorded yet.</p>
                    } @else {
                        @for (item of metrics.byUtilityOwner; track item.label) {
                            <div class="mb-2">
                                <div class="d-flex justify-content-between small">
                                    <span class="text-truncate me-2">{{ item.label }}</span>
                                    <span class="fw-semibold">{{ item.count }}</span>
                                </div>
                                <div class="du-track">
                                    <div class="du-track-fill" [style.width.%]="(item.count / maxLabelCount(metrics.byUtilityOwner)) * 100"></div>
                                </div>
                            </div>
                        }
                    }
                </div>
            </div>
        </div>

        <div class="col-lg-4">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Notification compliance</h5>
                    <div class="mb-2">
                        <div class="text-muted small text-uppercase">Safety dept notified</div>
                        <div class="fs-4 fw-semibold text-success">{{ metrics.notificationCompliance.safetyNotifiedRate }}%</div>
                        <div class="small text-muted">{{ metrics.notificationCompliance.safetyNotifiedCount }} of {{ metrics.notificationCompliance.total }}</div>
                    </div>
                    <ul class="list-unstyled mb-0 small">
                        <li class="d-flex justify-content-between border-bottom py-1">
                            <span>PM notified</span><span class="fw-semibold">{{ metrics.notificationCompliance.pmNotifiedCount }}</span>
                        </li>
                        <li class="d-flex justify-content-between border-bottom py-1">
                            <span>PE notified</span><span class="fw-semibold">{{ metrics.notificationCompliance.peNotifiedCount }}</span>
                        </li>
                        <li class="d-flex justify-content-between py-1">
                            <span>Fire &amp; EMS notified</span><span class="fw-semibold">{{ metrics.notificationCompliance.fireAndEmsNotifiedCount }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mt-0">
        <div class="col-12">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Recent damages</h5>
                    @if (metrics.recentDamages.length === 0) {
                        <p class="text-muted small mb-0">No damages recorded yet.</p>
                    } @else {
                        <div class="du-list-scroll">
                            <table class="table table-sm table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Job</th>
                                        <th>Utility type</th>
                                        <th>VanCon at fault</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (item of metrics.recentDamages; track item.id) {
                                        <tr>
                                            <td class="text-nowrap">
                                                @if (item.damageDate) {
                                                    {{ item.damageDate | date: 'mediumDate' }}
                                                } @else {
                                                    <span class="text-muted small">-</span>
                                                }
                                            </td>
                                            <td class="text-truncate">{{ item.jobName }}</td>
                                            <td class="text-truncate">{{ item.typeOfUtility }}</td>
                                            <td>{{ item.vanConAtFault }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
</div>
